import React, { useCallback, useMemo, useState } from 'react'
import { Button, Drawer, Dropdown, Flex, MenuProps } from 'antd'
import { CloudDownloadOutlined, DownOutlined, PrinterOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  CustomReport,
  CustomReportArgument,
  PrintStyleSettings
} from '../../../../../redux/context/customReports/types'
import { ResponsiveLayouts } from '../grid.utils'
import { getTitleByLanguage } from '../customReportTable/utils'
import { PAPER_DIMENSIONS } from './paperConfig'
import { PrintSettingsProvider, usePrintSettings } from './PrintSettingsContext'
import PrintSettingsPanel from './PrintSettingsPanel'
import PrintPreviewContent from './PrintPreviewContent'
import { formatAsDate } from '../../../../../config/dayjsConfig'
import useCustomReportExport from '../../hooks/useCustomReportExport'

const SIDEBAR_WIDTH = 240
const DRAWER_PADDING = 72

export interface PrintDrawerProps {
  report: CustomReport
  disabled?: boolean
  layouts?: ResponsiveLayouts
  buttonLabel?: React.ReactNode
  companyId: string
  companyName?: string
  contractId?: number | null
  token?: string | null
  date?: string | null
  language?: string
  args?: CustomReportArgument[] | null
  onSavePrintSettings?: (settings: PrintStyleSettings) => void
  saveLoading?: boolean
  downloadMenu?: MenuProps
  downloadLoading?: boolean
}

interface DrawerShellProps {
  title: React.ReactNode
  open: boolean
  onClose: () => void
  onAfterOpen: () => void
  children: React.ReactNode
}

const DrawerShell: React.FC<DrawerShellProps> = ({ title, open, onClose, onAfterOpen, children }) => {
  const { settings, handlePrint, handleServerExport, serverExportLoading, onSave, saveLoading } = usePrintSettings()
  const { t } = useTranslation()

  const drawerWidth = useMemo(() => {
    const paperWidthMm = PAPER_DIMENSIONS[settings.paperSize][settings.orientation === 'portrait' ? 'width' : 'height']
    return `calc(${paperWidthMm}mm + ${SIDEBAR_WIDTH + DRAWER_PADDING}px)`
  }, [settings.paperSize, settings.orientation])

  const handleAfterOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) onAfterOpen()
    },
    [onAfterOpen]
  )

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      maskClosable={false}
      afterOpenChange={handleAfterOpenChange}
      width={drawerWidth}
      styles={{ body: { overflow: 'hidden', padding: 0 } }}
      extra={
        <div data-print-ignore style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {handleServerExport && (
            <Button
              icon={<CloudDownloadOutlined />}
              loading={serverExportLoading}
              onClick={handleServerExport}
              title={t('actions:download', { format: settings.outputFormat.toUpperCase() })}
            />
          )}
          <Button icon={<PrinterOutlined />} onClick={handlePrint} title={t('actions:print')} />
          {onSave && (
            <Button type="primary" loading={saveLoading} onClick={onSave}>
              {t('actions:saveSettings')}
            </Button>
          )}
        </div>
      }
    >
      <div style={{ display: 'flex', height: '100%' }}>
        <PrintSettingsPanel />
        <div className="print-preview-area" style={{ flex: 1, overflow: 'auto', background: '#e8e8e8', padding: 24 }}>
          {children}
        </div>
      </div>
    </Drawer>
  )
}

const PrintDrawer: React.FC<PrintDrawerProps> = ({
  report,
  disabled = false,
  layouts,
  buttonLabel,
  companyId,
  companyName,
  contractId,
  token,
  date,
  language,
  args,
  onSavePrintSettings,
  saveLoading = false,
  downloadMenu,
  downloadLoading = false
}) => {
  const [visible, setVisible] = useState(false)
  const [drawerReady, setDrawerReady] = useState(false)
  const { t } = useTranslation()

  const { download: exportToFile, isLoading: serverExportLoading } = useCustomReportExport({
    reportId: report.id,
    companyId,
    companyName,
    contractId,
    date,
    language,
    args
  })

  const sectionEntries = useMemo(() => {
    const sections = report.sections || []
    const toEntry = (s: (typeof sections)[number]) => ({ id: s.id, name: getTitleByLanguage(s), type: s.type })
    const lg = layouts?.lg
    if (!lg) return sections.map(toEntry)

    const yMap = new Map(lg.map(item => [String(item.i), item.y]))
    return [...sections].sort((a, b) => (yMap.get(String(a.id)) ?? 0) - (yMap.get(String(b.id)) ?? 0)).map(toEntry)
  }, [report.sections, layouts])

  const handleServerExport = useCallback(
    async (exportSettings: PrintStyleSettings, forcedBreaks: Set<number>) => {
      await exportToFile({ printSettings: exportSettings, reportName: getTitleByLanguage(report), forcedBreaks })
    },
    [exportToFile, report]
  )

  const handleClose = () => {
    setVisible(false)
    setDrawerReady(false)
  }

  const Title = () => (
    <Flex align="center" style={{ gap: 0 }}>
      <div
        style={{
          width: SIDEBAR_WIDTH - 40,
          minWidth: SIDEBAR_WIDTH - 40,
          paddingRight: 16,
          marginRight: 16,
          borderRight: '1px solid #f0f0f0'
        }}
      >
        {t('customReportPrint:previewTitle')}
      </div>
      <div>
        <div style={{ fontWeight: 500, lineHeight: 1.3 }}>{getTitleByLanguage(report)}</div>
        {date && (
          <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400, lineHeight: 1.3 }}>{formatAsDate(date)}</div>
        )}
      </div>
    </Flex>
  )

  return (
    <PrintSettingsProvider
      open={visible}
      sections={sectionEntries}
      handleServerExport={handleServerExport}
      serverExportLoading={serverExportLoading}
      initialPrintSettings={report.style?.print}
      onSave={onSavePrintSettings ?? null}
      saveLoading={saveLoading}
      printTitle={[companyName, getTitleByLanguage(report), formatAsDate(date)].filter(Boolean).join('-')}
    >
      {downloadMenu ? (
        <Dropdown.Button
          icon={<DownOutlined />}
          menu={downloadMenu}
          loading={downloadLoading}
          disabled={disabled}
          onClick={() => setVisible(true)}
          title={t('customReportPrint:printAndExport')}
        >
          <PrinterOutlined />
          {buttonLabel ?? null}
        </Dropdown.Button>
      ) : (
        <Button
          icon={<PrinterOutlined />}
          disabled={disabled}
          onClick={() => setVisible(true)}
          title={buttonLabel ? undefined : t('customReportPrint:printAndExport')}
        >
          {buttonLabel ?? null}
        </Button>
      )}
      <DrawerShell title={<Title />} open={visible} onClose={handleClose} onAfterOpen={() => setDrawerReady(true)}>
        {visible && (
          <PrintPreviewContent
            report={report}
            drawerReady={drawerReady}
            companyId={companyId}
            companyName={companyName}
            contractId={contractId}
            token={token}
            date={date}
            language={language}
            args={args}
          />
        )}
      </DrawerShell>
    </PrintSettingsProvider>
  )
}

export default PrintDrawer
